import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  CATEGORIES, CLASSIFICATIONS, DELIVERY_MODES, DURATION_UNITS,
  PROGRAM_STATUSES, PAYMENT_PLANS, BATCH_STATUSES, ENROLMENT_STATUSES,
  PAYMENT_STATUSES, findLabel, findTone, formatDuration, formatMoney,
} from "@/lib/academyConstants";
import { toast } from "sonner";

/** Drawer for a single program. Handles View / Edit + tabs. */
export default function ProgramDrawer({ program, open, onClose, onSaved, canManage, canSeeFinancials }) {
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(program || {});
  const [batches, setBatches] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => { setF(program || {}); setEditing(false); setTab("overview"); }, [program]);
  useEffect(() => {
    if (!program?.id) return;
    api.get("/academy/batches", { params: { program_id: program.id } }).then(r => setBatches(r.data));
    api.get("/academy/enrolments", { params: { program_id: program.id } }).then(r => setEnrolments(r.data));
  }, [program?.id]);

  if (!program) return null;

  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const save = async () => {
    try {
      const payload = { ...f };
      ["fee", "partner_cost", "max_discount_pct", "capacity", "duration_value"].forEach(k => {
        if (payload[k] !== undefined && payload[k] !== null) payload[k] = Number(payload[k]);
      });
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      delete payload.current_enrolments; delete payload.archived; delete payload.gross_margin;
      const r = await api.patch(`/academy/programs/${program.id}`, payload);
      toast.success("Program updated");
      setEditing(false);
      onSaved?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const cls = findTone(CLASSIFICATIONS, program.classification);
  const st  = findTone(PROGRAM_STATUSES, program.status);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent side="right" className="bg-white w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="miro-overline">{program.program_code || "PROGRAM"}</div>
              <SheetTitle className="font-display text-2xl text-[var(--miro-navy)] leading-tight mt-1">
                {program.title}
              </SheetTitle>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge className="border-0 font-semibold text-[10px]" style={{ background: cls?.bg, color: cls?.fg }}>
                  {findLabel(CLASSIFICATIONS, program.classification)}
                </Badge>
                <Badge className="border-0 font-semibold text-[10px]" style={{ background: "#E0F2FE", color: "#0284C7" }}>
                  {findLabel(CATEGORIES, program.category)}
                </Badge>
                <Badge className="border-0 font-semibold text-[10px]" style={{ background: st?.bg, color: st?.fg }}>
                  {findLabel(PROGRAM_STATUSES, program.status)}
                </Badge>
              </div>
            </div>
            {canManage && (
              editing ? (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setEditing(false); setF(program); }}>Cancel</Button>
                  <Button size="sm" data-testid="program-drawer-save" onClick={save} style={{ background: "var(--miro-navy)" }}>Save</Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" data-testid="program-drawer-edit" onClick={() => setEditing(true)}>Edit</Button>
              )
            )}
          </div>
        </SheetHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList data-testid="program-drawer-tabs" className="bg-[var(--miro-bg)] border border-[var(--miro-border)] flex-wrap h-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="batches">Batches ({batches.length})</TabsTrigger>
            <TabsTrigger value="enrolments">Enrolments ({enrolments.length})</TabsTrigger>
            <TabsTrigger value="fees">Fees & Payments</TabsTrigger>
            <TabsTrigger value="curriculum">Curriculum</TabsTrigger>
            {canSeeFinancials && <TabsTrigger value="partner">Partner Details</TabsTrigger>}
            {canSeeFinancials && <TabsTrigger value="costs">Costs & Margin</TabsTrigger>}
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-3 text-sm">
            {editing ? (
              <EditForm f={f} upd={upd} canSeeFinancials={canSeeFinancials}/>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Program ID" value={program.id}/>
                <Field label="Code" value={program.program_code || "—"}/>
                <Field label="Category" value={findLabel(CATEGORIES, program.category)}/>
                <Field label="Classification" value={findLabel(CLASSIFICATIONS, program.classification)}/>
                <Field label="Delivery mode" value={findLabel(DELIVERY_MODES, program.delivery_mode)}/>
                <Field label="Delivery partner" value={program.delivery_partner || "—"}/>
                <Field label="Duration" value={formatDuration(program)}/>
                <Field label="Capacity" value={`${program.current_enrolments}/${program.capacity || 0}`}/>
                <Field label="Fee" value={formatMoney(program.fee)}/>
                <Field label="Max discount %" value={`${program.max_discount_pct || 0}%`}/>
                <Field label="Payment plan" value={findLabel(PAYMENT_PLANS, program.payment_plan)}/>
                <Field label="Batch window" value={program.batch_start_date
                  ? `${new Date(program.batch_start_date).toLocaleDateString()} → ${program.batch_end_date ? new Date(program.batch_end_date).toLocaleDateString() : "—"}`
                  : "—"}/>
                <div className="col-span-2">
                  <div className="miro-overline">Description</div>
                  <div className="text-[var(--miro-text-secondary)] mt-1">{program.description || "—"}</div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="batches" className="mt-4">
            <BatchesTab programId={program.id} program={program} canManage={canManage} batches={batches}
                        onChanged={() => api.get("/academy/batches", { params: { program_id: program.id } }).then(r => setBatches(r.data))}/>
          </TabsContent>

          <TabsContent value="enrolments" className="mt-4">
            <SimpleEnrolments enrolments={enrolments} program={program} canSeeFinancials={canSeeFinancials}/>
          </TabsContent>

          <TabsContent value="fees" className="mt-4 text-sm space-y-2">
            <Field label="Program fee"           value={formatMoney(program.fee)}/>
            <Field label="Max discount %"        value={`${program.max_discount_pct || 0}%`}/>
            <Field label="Payment plan"          value={findLabel(PAYMENT_PLANS, program.payment_plan)}/>
            <div className="text-[11px] text-[var(--miro-text-muted)] mt-2">
              GST is not treated as revenue. Track collections at the enrolment level.
            </div>
          </TabsContent>

          <TabsContent value="curriculum" className="mt-4 text-sm">
            {program.curriculum_url ? (
              <a href={program.curriculum_url} target="_blank" rel="noreferrer"
                 className="text-[var(--miro-blue)] font-semibold underline">
                Open curriculum document ↗
              </a>
            ) : (
              <div className="text-[var(--miro-text-muted)]">No curriculum document attached yet.</div>
            )}
          </TabsContent>

          {canSeeFinancials && (
            <TabsContent value="partner" className="mt-4 text-sm space-y-2">
              <Field label="Delivery partner" value={program.delivery_partner || "MIRO"}/>
              <Field label="Classification"   value={findLabel(CLASSIFICATIONS, program.classification)}/>
              <div className="text-[11px] text-[var(--miro-text-muted)] mt-2">
                Partner-level SLAs and commercial terms will surface here in the next iteration.
              </div>
            </TabsContent>
          )}

          {canSeeFinancials && (
            <TabsContent value="costs" className="mt-4 text-sm space-y-2">
              <Field label="Program fee"          value={formatMoney(program.fee)}/>
              <Field label="Partner cost / seat"  value={formatMoney(program.partner_cost)}/>
              <Field label="Gross margin / seat"  value={formatMoney(program.gross_margin)}/>
              <div className="text-[11px] text-[var(--miro-text-muted)] mt-2">
                Gross margin = Fee − Partner cost. Actual enrolment margin also subtracts discount.
              </div>
            </TabsContent>
          )}

          <TabsContent value="activity" className="mt-4 text-sm">
            <ActivityList programId={program.id}/>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="miro-overline">{label}</div>
      <div className="text-[var(--miro-navy)] font-medium mt-0.5">{value}</div>
    </div>
  );
}

function EditForm({ f, upd, canSeeFinancials }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2"><Label>Title *</Label><Input data-testid="program-edit-title" value={f.title || ""} onChange={e => upd("title", e.target.value)} className="mt-1"/></div>
      <div><Label>Program code</Label><Input data-testid="program-edit-code" value={f.program_code || ""} onChange={e => upd("program_code", e.target.value)} className="mt-1"/></div>
      <div>
        <Label>Category *</Label>
        <Select value={f.category} onValueChange={v => upd("category", v)}>
          <SelectTrigger data-testid="program-edit-category" className="mt-1"><SelectValue/></SelectTrigger>
          <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Classification *</Label>
        <Select value={f.classification} onValueChange={v => upd("classification", v)}>
          <SelectTrigger data-testid="program-edit-classification" className="mt-1"><SelectValue/></SelectTrigger>
          <SelectContent>{CLASSIFICATIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Delivery mode *</Label>
        <Select value={f.delivery_mode} onValueChange={v => upd("delivery_mode", v)}>
          <SelectTrigger data-testid="program-edit-mode" className="mt-1"><SelectValue/></SelectTrigger>
          <SelectContent>{DELIVERY_MODES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Delivery partner</Label><Input value={f.delivery_partner || ""} onChange={e => upd("delivery_partner", e.target.value)} className="mt-1"/></div>
      <div><Label>Duration *</Label><Input data-testid="program-edit-duration" type="number" min={0} value={f.duration_value ?? 0} onChange={e => upd("duration_value", e.target.value)} className="mt-1"/></div>
      <div>
        <Label>Duration unit</Label>
        <Select value={f.duration_unit || "weeks"} onValueChange={v => upd("duration_unit", v)}>
          <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
          <SelectContent>{DURATION_UNITS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Fee (₹) *</Label><Input data-testid="program-edit-fee" type="number" min={0} value={f.fee ?? 0} onChange={e => upd("fee", e.target.value)} className="mt-1"/></div>
      <div><Label>Max discount (%)</Label><Input type="number" min={0} max={100} value={f.max_discount_pct ?? 0} onChange={e => upd("max_discount_pct", e.target.value)} className="mt-1"/></div>
      {canSeeFinancials && (
        <div><Label>Partner cost / seat (₹)</Label><Input type="number" min={0} value={f.partner_cost ?? 0} onChange={e => upd("partner_cost", e.target.value)} className="mt-1"/></div>
      )}
      <div><Label>Capacity</Label><Input type="number" min={0} value={f.capacity ?? 0} onChange={e => upd("capacity", e.target.value)} className="mt-1"/></div>
      <div>
        <Label>Status *</Label>
        <Select value={f.status || "active"} onValueChange={v => upd("status", v)}>
          <SelectTrigger data-testid="program-edit-status" className="mt-1"><SelectValue/></SelectTrigger>
          <SelectContent>{PROGRAM_STATUSES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Payment plan</Label>
        <Select value={f.payment_plan || "one_time"} onValueChange={v => upd("payment_plan", v)}>
          <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
          <SelectContent>{PAYMENT_PLANS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="col-span-2"><Label>Curriculum attachment URL</Label><Input value={f.curriculum_url || ""} onChange={e => upd("curriculum_url", e.target.value)} className="mt-1" placeholder="https://…"/></div>
      <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={f.description || ""} onChange={e => upd("description", e.target.value)} className="mt-1"/></div>
    </div>
  );
}

function BatchesTab({ programId, program, canManage, batches, onChanged }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ batch_code: "", start_date: "", end_date: "", capacity: 0, delivery_mode: program?.delivery_mode || "hybrid", trainer: "", schedule: "", status: "planned", notes: "" });

  const submit = async () => {
    try {
      await api.post("/academy/batches", { ...f, program_id: programId, capacity: Number(f.capacity) || 0 });
      toast.success("Batch created");
      setOpen(false);
      setF({ batch_code: "", start_date: "", end_date: "", capacity: 0, delivery_mode: program?.delivery_mode || "hybrid", trainer: "", schedule: "", status: "planned", notes: "" });
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  return (
    <div>
      {canManage && (
        <div className="flex justify-end mb-3">
          {open ? (
            <div className="w-full grid grid-cols-2 gap-2 border border-[var(--miro-border)] rounded-lg p-3 bg-[var(--miro-bg)]">
              <Input placeholder="Batch code" data-testid="batch-new-code" value={f.batch_code} onChange={e => setF({ ...f, batch_code: e.target.value })}/>
              <Input type="date" data-testid="batch-new-start" value={f.start_date} onChange={e => setF({ ...f, start_date: e.target.value })}/>
              <Input type="date" data-testid="batch-new-end" value={f.end_date} onChange={e => setF({ ...f, end_date: e.target.value })}/>
              <Input type="number" min={0} placeholder="Capacity" data-testid="batch-new-capacity" value={f.capacity} onChange={e => setF({ ...f, capacity: e.target.value })}/>
              <Input placeholder="Trainer" value={f.trainer} onChange={e => setF({ ...f, trainer: e.target.value })}/>
              <Input placeholder="Schedule (e.g. Mon/Wed/Fri 7-9pm)" value={f.schedule} onChange={e => setF({ ...f, schedule: e.target.value })}/>
              <Select value={f.delivery_mode} onValueChange={v => setF({ ...f, delivery_mode: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{DELIVERY_MODES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={f.status} onValueChange={v => setF({ ...f, status: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{BATCH_STATUSES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <div className="col-span-2 flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button size="sm" data-testid="batch-new-submit" onClick={submit} style={{ background: "var(--miro-navy)" }}>Create batch</Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" data-testid="batch-add-btn" onClick={() => setOpen(true)}>+ Add batch</Button>
          )}
        </div>
      )}
      <div className="space-y-2">
        {batches.length === 0 && <div className="text-[var(--miro-text-muted)] text-sm">No batches yet.</div>}
        {batches.map(b => {
          const tone = findTone(BATCH_STATUSES, b.status);
          return (
            <div key={b.id} data-testid={`batch-row-${b.id}`} className="border border-[var(--miro-border)] rounded-lg p-3 bg-white flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[var(--miro-navy)] text-sm">{b.batch_code || `Batch ${b.id.slice(0, 6)}`}</div>
                <div className="text-xs text-[var(--miro-text-muted)]">
                  {b.start_date ? new Date(b.start_date).toLocaleDateString() : "—"} → {b.end_date ? new Date(b.end_date).toLocaleDateString() : "—"} · {findLabel(DELIVERY_MODES, b.delivery_mode)} · {b.trainer || "—"}
                </div>
                {b.schedule && <div className="text-xs text-[var(--miro-text-secondary)] mt-0.5">{b.schedule}</div>}
              </div>
              <div className="text-xs font-mono text-[var(--miro-navy)]">{b.current_enrolments}/{b.capacity || 0}</div>
              <Badge className="border-0 font-semibold text-[10px]" style={{ background: tone?.bg, color: tone?.fg }}>
                {findLabel(BATCH_STATUSES, b.status)}
              </Badge>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SimpleEnrolments({ enrolments, program, canSeeFinancials }) {
  if (!enrolments.length) return <div className="text-[var(--miro-text-muted)] text-sm">No enrolments yet.</div>;
  return (
    <div className="space-y-2">
      {enrolments.map(e => {
        const pay = findTone(PAYMENT_STATUSES, e.payment_status);
        return (
          <div key={e.id} data-testid={`prog-drawer-enrol-${e.id}`} className="border border-[var(--miro-border)] rounded-lg p-3 bg-white flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[var(--miro-navy)] text-sm">{e.student_name}</div>
              <div className="text-xs text-[var(--miro-text-muted)]">
                {e.student_id || "—"} · {findLabel(ENROLMENT_STATUSES, e.status)} · Attendance {e.attendance_pct || 0}%
              </div>
            </div>
            {canSeeFinancials && (
              <div className="text-xs font-mono text-[var(--miro-text-secondary)]">
                Paid {formatMoney(e.amount_paid)} · Bal {formatMoney(e.balance)}
              </div>
            )}
            <Badge className="border-0 font-semibold text-[10px]" style={{ background: pay?.bg, color: pay?.fg }}>
              {findLabel(PAYMENT_STATUSES, e.payment_status)}
            </Badge>
          </div>
        );
      })}
    </div>
  );
}

function ActivityList({ programId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    // The audit endpoint might not exist yet; if the request fails silently ignore.
    api.get(`/communications`, { params: { related_type: "program", related_id: programId } })
      .then(r => setItems(r.data)).catch(() => setItems([]));
  }, [programId]);
  if (!items.length) return <div className="text-[var(--miro-text-muted)] text-sm">No activity recorded yet.</div>;
  return (
    <div className="space-y-2">
      {items.map(m => (
        <div key={m.id} className="border border-[var(--miro-border)] rounded-lg p-3 bg-white text-sm">
          <div className="font-semibold text-[var(--miro-navy)]">{m.kind}</div>
          <div className="text-xs text-[var(--miro-text-muted)]">{new Date(m.created_at).toLocaleString()}</div>
          <div className="mt-1 text-[var(--miro-text-secondary)]">{m.content}</div>
        </div>
      ))}
    </div>
  );
}
